// Ekran 02 — "Yeni Kabul" sekmesi (pane#pane-yeni).
// İskelet referanstan: .crumb + .d-head + .d-grid(sol .card'lar / sağ .inst + .side-card)

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDrawer } from '../../components/Drawer'
import { useSekmeler } from '../../components/Sekmeler'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, sayiyaCevir } from '../../lib/trFormat'
import { filo } from '../fleet/api'
import { cariler as cariApi, havuzlar as havuzApi, stokKartlari } from '../masterdata/api'
import { HizliCariDrawer } from './MalKabulDrawerlari'
import { lotlar } from './api'

/**
 * Formun başlangıç değerleri — reddedilen/storno bir kabulü "yeni kabul olarak
 * kopyala" akışı (ekran 02 paneli, ref:643) buradan besleniyor. Tartım
 * BİLEREK taşınmıyor: yeni parti yeniden tartılır.
 */
export type YeniKabulBaslangici = {
  havuzId?: string
  plaka?: string
  surucu?: string
  fiyat?: string
}

export default function YeniKabulSekmesi({
  sekmeId,
  baslangic,
}: {
  sekmeId: string
  baslangic?: YeniKabulBaslangici
}) {
  const kuyruk = useQueryClient()
  const sekmeler = useSekmeler()
  const { ac } = useDrawer()

  const [havuzId, setHavuzId] = useState(baslangic?.havuzId ?? '')
  // Cari seçimi havuzdan TÜRETİLEBİLİR olduğu için state'te yalnız "kullanıcı
  // ne seçti" tutuluyor; kopyalama akışı sadece havuzId taşıdığından açılışta
  // cari onun üzerinden çözülür (aşağıdaki `cariId`).
  const [cariSecim, setCariSecim] = useState<string | null>(null)
  // Substrat artık HAVUZ değil STOK KARTI seçiyor; havuz ondan doğuyor.
  const [stokSecim, setStokSecim] = useState('')
  // Kopyalanan kayıtta plaka zaten elde: araç doğrudan "Harici" dalında açılır,
  // filodan yeniden seçtirmek gereksiz bir adım olurdu.
  const [aracFilodan, setAracFilodan] = useState(!baslangic?.plaka)
  const [plaka, setPlaka] = useState(baslangic?.plaka ?? '')
  const [surucu, setSurucu] = useState(baslangic?.surucu ?? '')
  const [brut, setBrut] = useState('')
  const [dara, setDara] = useState('')
  const [fiyat, setFiyat] = useState(baslangic?.fiyat ?? '')

  const { data: havuzlar = [] } = useQuery({
    queryKey: ['havuzlar'],
    queryFn: () => havuzApi.liste(),
  })

  // "Filodan" dalı gerçek araç listesini kullanır (K35 varlıkları Faz 5'te
  // bağlandı); seçim hem plakayı hem atanmış şoförü doldurur.
  const { data: araclar = [] } = useQuery({ queryKey: ['araclar'], queryFn: filo.araclar })

  const secili = havuzlar.find((h) => h.id === havuzId)

  // Referans iki kademeli seçiyor (ref:726-728): önce Cari, sonra o carinin
  // substratı. İki liste de mevcut havuz yanıtından türetiliyor — K26 gereği
  // giriş yine tek bir firma×ürün havuzuna (`havuzId`) yazılır.
  /**
   * Cari listesi CARİ TABLOSUNDAN geliyor, havuzlardan değil.
   *
   * Eskiden havuzlardan türetiliyordu ve yeni bir tesiste havuz olmadığı için
   * liste BOŞ kalıyordu — müşteri ilk mal kabulünü hiç yapamıyordu (ölçüldü:
   * 71 cari, 0 havuz). K27 zaten "ilk LOT'ta havuz doğar" diyor; havuzun
   * seçimden ÖNCE var olmasını beklemek o kuralla çelişiyordu.
   *
   * Süzgeç "atık sağlayıcı": hammaddeyi getiren rol bu (§8-04).
   */
  const { data: atikCarileri = [] } = useQuery({
    queryKey: ['cariler', 'atik'],
    queryFn: () => cariApi.liste(undefined, 'atik'),
  })

  /** Substrat listesi: hammadde kartları. */
  const { data: hammaddeler = [] } = useQuery({
    queryKey: ['kartlar', 'hammadde', ''],
    queryFn: () => stokKartlari.liste('hammadde'),
  })

  const cariler = useMemo(
    () =>
      atikCarileri
        .map((c) => ({ id: c.id, unvan: c.unvan }))
        .sort((a, b) => a.unvan.localeCompare(b.unvan, 'tr')),
    [atikCarileri],
  )

  const cariId = cariSecim ?? secili?.cariId ?? ''
  const stokKartiId = stokSecim || secili?.stokKartiId || ''

  // Seçilen çiftin havuzu VARSA onu kullan; yoksa sunucu taslak açarken
  // doğuracak (K27). Etkin değerler ancak havuz varken gösterilebiliyor.
  const eslesenHavuz = useMemo(
    () => havuzlar.find((h) => h.cariId === cariId && h.stokKartiId === stokKartiId),
    [havuzlar, cariId, stokKartiId],
  )

  function cariSec(yeni: string) {
    setCariSecim(yeni)
    // Cari değişince substrat sıfırlanıyor: eski seçim kalsaydı başka bir
    // firmanın eşleşmesi kayda taşınırdı.
    setStokSecim('')
    setHavuzId('')
  }

  function stokSec(yeni: string) {
    setStokSecim(yeni)
    const h = havuzlar.find((x) => x.cariId === cariId && x.stokKartiId === yeni)
    setHavuzId(h?.id ?? '')
  }

  /**
   * K13 etkin değerleri — anahtar HAVUZ değil KART.
   *
   * Havuza bağlıyken, henüz havuzu olmayan bir eşleşmede önizleme boş
   * kalıyordu: kullanıcı substratı seçiyor ve hiçbir şey görmüyordu. Kart
   * kodu her iki durumda da elde olduğu için önizleme ilk kabulde de çalışıyor
   * (havuz analizi yoksa K13 zinciri tesis değerine, o da yoksa literatüre
   * düşüyor).
   */
  const seciliKart = useMemo(
    () => hammaddeler.find((k) => k.id === stokKartiId),
    [hammaddeler, stokKartiId],
  )

  const { data: teorik } = useQuery({
    queryKey: ['teorik', seciliKart?.kod],
    queryFn: () => stokKartlari.teorik(seciliKart!.kod),
    enabled: !!seciliKart,
  })

  // Hijyen bayrağı kartta (K19) — teorik hesap DTO'su taşımıyor.
  const { data: kart } = useQuery({
    queryKey: ['kart', seciliKart?.kod],
    queryFn: () => stokKartlari.detay(seciliKart!.kod),
    enabled: !!seciliKart,
  })

  const brutSayi = brut.trim() === '' ? null : sayiyaCevir(brut)
  const daraSayi = dara.trim() === '' ? null : sayiyaCevir(dara)
  const net =
    brutSayi !== null && daraSayi !== null && brutSayi > daraSayi
      ? (brutSayi - daraSayi) / 1000
      : null

  // Referans calcNew(): potansiyel net tonaj × 1 ton değerleriyle çarpılır.
  const potansiyel = useMemo(() => {
    if (net === null || !teorik) return null
    const c = (v: number | null) => (v === null ? null : v * net)
    return { biyogaz: c(teorik.biyogaz), metan: c(teorik.metan), elektrik: c(teorik.elektrik) }
  }, [net, teorik])

  const kaydet = useMutation({
    mutationFn: async (onayla: boolean) => {
      const lot = await lotlar.olustur({
        // Havuz varsa kimliği, yoksa çift gidiyor — sunucu K27 gereği doğuruyor.
        havuzId: eslesenHavuz?.id ?? '00000000-0000-0000-0000-000000000000',
        cariId,
        stokKartiId,
        // Filodan/harici ayrımı yalnız plakanın NASIL girildiğini değiştirir;
        // kayda giden alan tek: plaka.
        plaka: plaka || null,
        surucu: surucu || null,
        brut: brutSayi,
        dara: daraSayi,
        birimFiyat: fiyat.trim() === '' ? null : sayiyaCevir(fiyat),
      })
      if (!onayla) return { lot, mesaj: 'Taslak olarak kaydedildi' }
      const sonuc = await lotlar.onayla(lot.id)
      return { lot: sonuc.lot, mesaj: sonuc.mesaj }
    },
    onSuccess: ({ mesaj }) => {
      kuyruk.invalidateQueries({ queryKey: ['lotlar'] })
      kuyruk.invalidateQueries({ queryKey: ['lot-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['lot-sayaclari'] })
      toast(mesaj)
      sekmeler.kapat(sekmeId)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Kabul kaydedilemedi'),
  })

  // Onay kapısı sunucuda da var (dara zorunlu → 409). Buradaki kilit yalnız
  // gereksiz gidiş dönüşü önlüyor.
  const secimTam = cariId !== '' && stokKartiId !== ''
  const onaylanabilir = secimTam && net !== null

  return (
    <>
      <div className="crumb">
        Operasyon / Mal Kabul <b>/ Yeni Kabul</b>
      </div>

      <div className="d-head">
        <h1 className="d-title">Yeni Kabul</h1>
        <span className="d-kod">taslak</span>
        <div className="page-actions">
          <button
            className="btn btn-sec btn-sm"
            type="button"
            disabled={!secimTam || kaydet.isPending}
            title={secimTam ? undefined : 'Önce cari ve substrat seçin'}
            onClick={() => kaydet.mutate(false)}
          >
            Taslak kaydet
          </button>
          <button
            className="btn btn-pri btn-sm"
            type="button"
            disabled={!onaylanabilir || kaydet.isPending}
            title={onaylanabilir ? undefined : 'Onay için brüt ve dara tartımı zorunludur'}
            onClick={() => kaydet.mutate(true)}
          >
            Onayla → Lot oluştur
          </button>
        </div>
      </div>

      <div className="d-grid">
        <div>
          <div className="card">
            <div className="sect-t">Cari &amp; Substrat</div>
            <div className="form-g" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="fld">
                <label>Cari</label>
                <div className="with-plus">
                  <select
                    className="in"
                    value={cariId}
                    onChange={(e) => cariSec(e.target.value)}
                  >
                    <option value="">Seçin…</option>
                    {cariler.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.unvan}
                      </option>
                    ))}
                  </select>
                  <button
                    className="plus-btn"
                    type="button"
                    title="İşlemi bozmadan yeni cari ekle"
                    onClick={() =>
                      ac({
                        baslik: 'Yeni cari — hızlı kayıt',
                        altBaslik: 'Mevcut kabul formunuz açık kalır',
                        boyut: 'sm',
                        icerik: (
                          <HizliCariDrawer
                            secildi={() =>
                              kuyruk.invalidateQueries({ queryKey: ['havuzlar'] })
                            }
                          />
                        ),
                      })
                    }
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                </div>
                <div className="fld-hint">
                  K26: giriş bir firma×ürün eşleşmesine yazılır. Eşleşmenin havuzu
                  yoksa ilk kabulde kendiliğinden açılır (K27).
                </div>
              </div>

              <div className="fld">
                <label>Substrat</label>
                <select
                  className="in"
                  value={stokKartiId}
                  disabled={cariId === ''}
                  title={cariId === '' ? 'Önce cari seçin' : undefined}
                  onChange={(e) => stokSec(e.target.value)}
                >
                  <option value="">Seçin…</option>
                  {hammaddeler.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.kod} {k.ad}
                    </option>
                  ))}
                </select>
                <div className="fld-hint">
                  {!seciliKart ? (
                    'Substrat seçilince etkin değerler burada görünür.'
                  ) : teorik ? (
                    <>
                      Etkin değerler: KM {tr(teorik.km.deger, 1)} · OKM{' '}
                      {tr(teorik.okmYuzde.deger, 0)} · {tr(teorik.verim.deger, 0)} Nm³/t oKM
                    </>
                  ) : (
                    'Etkin değerler yükleniyor…'
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 10 }}>
            <div className="sect-t">Araç</div>
            <div className="form-g" style={{ gridTemplateColumns: 'auto 1fr 1fr', alignItems: 'end' }}>
              <div className="fld">
                <label>Kaynak</label>
                <div className="radios">
                  <button
                    className={aracFilodan ? 'radio-i on' : 'radio-i'}
                    type="button"
                    onClick={() => {
                      setAracFilodan(true)
                      // Elle yazılmış plaka listede yoksa seçim "boş" görünürdü
                      // ama değer kayda giderdi — sessiz uyumsuzluk.
                      if (!araclar.some((a) => a.plaka === plaka)) setPlaka('')
                    }}
                  >
                    Filodan
                  </button>
                  <button
                    className={aracFilodan ? 'radio-i' : 'radio-i on'}
                    type="button"
                    onClick={() => setAracFilodan(false)}
                  >
                    Harici
                  </button>
                </div>
              </div>
              {/*
                Referans bu hücreyi takas ediyor (ref:733-734 · `aracTip()`):
                "Filodan" seçiliyken araç listesi, "Harici" seçiliyken serbest
                plaka girişi. Radyo grubu daha önce yalnız bir ipucu metnini
                değiştiriyordu, yani yan etkisizdi.
              */}
              {aracFilodan ? (
                <div className="fld">
                  <label>Araç</label>
                  <select
                    className="in"
                    value={plaka}
                    disabled={araclar.length === 0}
                    title={araclar.length === 0 ? 'Filoda kayıtlı araç yok' : undefined}
                    onChange={(e) => {
                      const a = araclar.find((x) => x.plaka === e.target.value)
                      setPlaka(e.target.value)
                      // Referansın "Araç · Şoför" seçeneği ikisini birlikte
                      // taşıyor; burada şoför alanı doluyor ama kilitlenmiyor —
                      // aracı vekâleten başkası kullanabilir.
                      if (a?.surucuAd) setSurucu(a.surucuAd)
                    }}
                  >
                    <option value="">Seçin…</option>
                    {araclar.map((a) => (
                      <option key={a.id} value={a.plaka}>
                        {a.plaka} · {a.surucuAd ?? '—'}
                      </option>
                    ))}
                  </select>
                  <div className="fld-hint">Seçim plakayı ve atanmış şoförü doldurur.</div>
                </div>
              ) : (
                <div className="fld">
                  <label>Plaka</label>
                  <input
                    className="in mono"
                    placeholder="42 XX 000"
                    value={plaka}
                    onChange={(e) => setPlaka(e.target.value)}
                  />
                </div>
              )}
              <div className="fld">
                <label>Şoför</label>
                <input
                  className="in"
                  placeholder="Ad Soyad"
                  value={surucu}
                  onChange={(e) => setSurucu(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 10 }}>
            <div className="sect-t">Tartım</div>
            <div className="form-g" style={{ gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'end' }}>
              <div className="fld">
                <label>Brüt (kg)</label>
                <input
                  className="in mono"
                  placeholder="0"
                  value={brut}
                  onChange={(e) => setBrut(e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Dara (kg)</label>
                <input
                  className="in mono"
                  placeholder="0"
                  value={dara}
                  onChange={(e) => setDara(e.target.value)}
                />
              </div>
              <div className="fld">
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  disabled
                  title="Kantar entegrasyonu Faz 2 (K7) — adaptör hazır"
                >
                  ⚖ Kantardan al
                </button>
              </div>
            </div>
            <div style={{ padding: '0 12px 12px' }}>
              <div className="netbar">
                <span className="nb-l">Net</span>
                <b>{tr(net, 3)}</b>
                <small>ton · brüt − dara otomatik</small>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 10 }}>
            <div className="sect-t">Opsiyonel</div>
            {/*
              Referansta üç sütun var (Birim fiyat · Görsel kontrol · Not) ama
              diğer ikisinin şemada karşılığı yok (§5.1 `lot`); tek alan üç
              sütunluk ızgarada kartın 1/3'üne sıkışıyordu.
            */}
            <div className="form-g" style={{ gridTemplateColumns: '1fr' }}>
              <div className="fld">
                <label>Birim fiyat (₺/t)</label>
                <input
                  className="in mono"
                  placeholder="—"
                  value={fiyat}
                  onChange={(e) => setFiyat(e.target.value)}
                />
                <div className="fld-hint">K12: opsiyonel; boşsa HAO değişmez.</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="inst">
            <div className="inst-t">Teorik potansiyel — canlı</div>
            <div className="inst-3">
              <div className="inst-c">
                <div className="inst-v">{tr(potansiyel?.biyogaz, 0)}</div>
                <div className="inst-l">Nm³ biyogaz</div>
              </div>
              <div className="inst-c">
                <div className="inst-v">{tr(potansiyel?.metan, 0)}</div>
                <div className="inst-l">Nm³ metan</div>
              </div>
              <div className="inst-c">
                <div className="inst-v">{tr(potansiyel?.elektrik, 0)}</div>
                <div className="inst-l">kWh elektrik</div>
              </div>
            </div>
            <div className="inst-note">
              Net tonaj ve substratın etkin değerleriyle anlık hesaplanır
              {teorik?.metanKaynagi === 'bmp' ? ' · metan ölçülen BMP’den (K31)' : ''}
            </div>
          </div>

          {/* K19 uyarısı — hijyen bayraklı substrat */}
          {secili && kart?.hij && (
            <div
              className="info-box"
              style={{
                marginTop: 10,
                background: 'var(--amber-bg)',
                borderColor: 'var(--amber-bd)',
                color: 'var(--amber)',
              }}
            >
              <div>
                <b>K19:</b> Bu substrat beslemeden önce hijyenizasyon partisi gerektirir
                (70 °C · 60 dk).
              </div>
            </div>
          )}

          <div className="side-card" style={{ marginTop: 10 }}>
            <div className="sect-t">Onayda ne olur?</div>
            <div className="mini-list" style={{ padding: '4px 0' }}>
              <div className="mini-li" style={{ padding: '6px 12px' }}>
                Lot oluşur<span className="vv">LOT-YYYY-####</span>
              </div>
              <div className="mini-li" style={{ padding: '6px 12px' }}>
                Stok girişi + havuz güncellenir<span className="vv plus">K6 · HAO</span>
              </div>
              <div className="mini-li" style={{ padding: '6px 12px' }}>
                Numune alma tek tık<span className="vv">NUM-…</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
